import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Eye } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import { useOptionalDashboardRole } from "./DashboardRoleProvider";

/**
 * Header "Viewing as: <Role>" control. Rendered only for multi-hat users
 * (and ADMIN, whose list spans every role — the support/QA view-as mode).
 * Switching re-keys the dashboard recipe and persists per user; it never
 * changes permissions or data access — gating stays per-widget on the
 * caller's own claims, server-side.
 */
export function RoleSwitcher() {
  const ctx = useOptionalDashboardRole();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!ctx || !ctx.canSwitch || !ctx.activeRole) return null;
  const { activeRole, setActiveRole, viewAsOptions, roleTitle } = ctx;

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-space-2 h-10 px-space-3 rounded-md",
          "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
          "focus-visible:outline-none focus-visible:shadow-goldRing",
        )}
      >
        <Eye size={16} strokeWidth={1.5} aria-hidden />
        <span className="text-label font-display uppercase tracking-label">
          Viewing as: <span className="text-gold-highlight">{roleTitle(activeRole)}</span>
        </span>
        <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="View dashboard as role"
          className={cn(
            "absolute right-0 mt-space-2 w-72 max-h-96 overflow-y-auto rounded-md shadow-lg z-50",
            "border border-border-light bg-white-pure text-onyx",
          )}
        >
          {viewAsOptions.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === activeRole}
              onClick={() => {
                setActiveRole(code);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center justify-between gap-space-2 px-space-3 py-space-2 text-small text-left hover:bg-white-warm",
                code === activeRole && "font-semibold",
              )}
            >
              <span className="truncate">
                <span className="font-display text-label uppercase tracking-label text-gray-mid mr-space-2">
                  {code}
                </span>
                {roleTitle(code)}
              </span>
              {code === activeRole && (
                <Check size={14} strokeWidth={2} className="text-gold-shadow shrink-0" aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
